import { useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import { fl } from "@/lib/finI18n";
import { coaName } from "@/lib/masterI18n";
import { focusNext } from "@/lib/formKeys";
import { AmountInput, fmtAmount } from "@/components/AmountInput";
import { ComboBox } from "@/components/ComboBox";
import { LedgerInput } from "@/components/LedgerInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";

export const isBlankLine = (l) => !(l.ledger || "").trim() && !l.coa_id && !Number(l.amount) && !(l.note || "").trim();

export function lineTotals(lines) {
  const sum = (dc) => Math.round(lines.reduce((s, l) => s + (l.dc === dc ? Number(l.amount) || 0 : 0), 0) * 100) / 100;
  return { td: sum("dr"), tc: sum("cr") };
}

const money = (v, cur, lang) => (cur && cur !== "IDR" ? `${fmtAmount(v, lang)} ${cur}` : `Rp ${fmtAmount(v, lang)}`);
// Desktop: Dr/Cr · Ledger (widest) · Account · Note · Amount · remove on one line; mobile stacks them.
const ROW = "grid grid-cols-12 gap-2 px-3 py-2 items-center md:grid-cols-[4rem_minmax(0,2.4fr)_minmax(0,1.7fr)_minmax(0,1.5fr)_minmax(0,1.35fr)_2rem]";

function DcCell({ dc, onToggle, testid }) {
  const dr = dc === "dr";
  return (
    <button type="button" data-field="dc" onClick={onToggle} data-testid={testid}
      className={`h-9 w-full rounded-md border text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${dr
        ? "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300" : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"}`}>
      {dr ? "Dr" : "Cr"}
    </button>
  );
}

// Tally Prime-style voucher lines: Dr/Cr · Vendor/Ledger (wide) · Account · Note · Amount on one row.
// Enter moves on (the note is optional and skipped); Enter on the last amount adds a row; Enter on an empty ledger ends the list;
// D / C switches the side.
export function TallyLines({ clientId, coa, lines, onChange, newLine, minRows = 1, testid, cur, ledgerFillsCoa, footer }) {
  const { t, lang } = useI18n();
  const box = useRef(null);
  const [focusRow, setFocusRow] = useState(null);
  const opts = useMemo(() => coa.map((c) => ({ value: c.id, label: `${c.code} · ${coaName(c, lang)}` })), [coa, lang]);
  const setLine = (i, patch) => onChange(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const cell = (i, col) => box.current?.querySelector(`[data-row="${i}"] [data-col="${col}"] :is(input, button)`);
  const { td, tc } = lineTotals(lines);

  useEffect(() => {
    if (focusRow === null) return;
    cell(focusRow, "dc")?.focus();
    setFocusRow(null);
  }, [focusRow, lines.length]);

  const addRow = (i) => { onChange([...lines, newLine(lines)]); setFocusRow(i + 1); };
  const onKey = (i) => (e) => {
    const col = e.target.closest?.("[data-col]")?.dataset.col;
    if (col === "dc") {
      const k = (e.key || "").toLowerCase();
      if ((k === "d" || k === "c") && !e.ctrlKey && !e.metaKey) { e.preventDefault(); setLine(i, { dc: k === "d" ? "dr" : "cr" }); return; }
      if (e.key === "Enter") { e.preventDefault(); focusNext(e.target); }
      return;
    }
    if (e.key !== "Enter" || e.defaultPrevented || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
    if (col === "ledger" && i >= minRows && isBlankLine(lines[i])) {
      e.preventDefault();
      onChange(lines.filter((_, idx) => idx !== i));
      setTimeout(() => focusNext(box.current?.querySelector(`[data-testid="${testid}-add-line"]`)), 60);
    } else if (col === "amount") {
      e.preventDefault();
      if (i === lines.length - 1) addRow(i);
      else cell(i + 1, "dc")?.focus();
    }
  };
  const pickLedger = (i, l) => (led) => {
    const fill = led.coa_id && (ledgerFillsCoa || !l.coa_id);
    setLine(i, { ledger: led.name, ...(fill ? { coa_id: led.coa_id } : {}) });
    if (fill || l.coa_id) setTimeout(() => { const a = cell(i, "amount"); a?.focus(); a?.select?.(); }, 70);
  };

  return (
    <div ref={box} className="rounded-md border border-border divide-y divide-border" data-testid={`${testid}-lines`}>
      <div className={`hidden md:grid ${ROW.replace("grid grid-cols-12 ", "").replace("py-2", "py-1.5")} text-[11px] text-muted-foreground`}>
        <span>{fl("tlDc", lang)}</span><span>{fl("tlLedger", lang)}</span><span>{fl("account", lang)}</span>
        <span>{fl("tlNote", lang)}</span><span className="text-right">{t("tf.amount")}{cur && cur !== "IDR" ? ` (${cur})` : ""}</span><span />
      </div>
      {lines.map((l, i) => (
        <div key={i} data-row={i} onKeyDown={onKey(i)} className={ROW} data-testid={`${testid}-line-${i}`}>
          <div data-col="dc" className="col-span-2 md:col-span-1">
            <DcCell dc={l.dc} onToggle={() => setLine(i, { dc: l.dc === "dr" ? "cr" : "dr" })} testid={`${testid}-line-dc-${i}`} />
          </div>
          <div data-col="ledger" className="col-span-10 md:col-span-1 min-w-0">
            <LedgerInput clientId={clientId} value={l.ledger || ""} testid={`${testid}-line-ledger-${i}`} placeholder={fl("ledger", lang)}
              onChange={(v) => setLine(i, { ledger: v })} onPick={pickLedger(i, l)} />
          </div>
          <div data-col="coa" className="col-span-7 md:col-span-1 min-w-0">
            <ComboBox testid={`${testid}-line-coa-${i}`} value={l.coa_id} onChange={(v) => setLine(i, { coa_id: v })} placeholder={t("tf.pickAccount")}
              searchPlaceholder={fl("searchCoa", lang)} options={opts} />
          </div>
          <div data-col="note" className="col-span-11 order-1 md:order-none md:col-span-1 min-w-0">
            <Input value={l.note || ""} maxLength={200} data-skip-enter="" placeholder={fl("tlNotePh", lang)} className="text-xs"
              onChange={(e) => setLine(i, { note: e.target.value })} data-testid={`${testid}-line-note-${i}`} />
          </div>
          <div data-col="amount" className="col-span-5 md:col-span-1 relative">
            <AmountInput className="num text-right pr-9" value={l.amount} placeholder={fmtAmount(0, lang)} onChange={(v) => setLine(i, { amount: v })}
              data-testid={`${testid}-line-amount-${i}`} />
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-muted-foreground">{l.dc === "dr" ? "Dr" : "Cr"}</span>
          </div>
          <div className="col-span-1 order-1 md:order-none flex justify-end">
            {lines.length > minRows && (
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" tabIndex={-1} onClick={() => onChange(lines.filter((_, idx) => idx !== i))}
                data-testid={`${testid}-line-remove-${i}`}><X className="h-4 w-4" /></Button>
            )}
          </div>
        </div>
      ))}
      <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-secondary/40 text-xs font-semibold items-center">
        <div className="col-span-12 md:col-span-6 flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" size="sm" onClick={() => addRow(lines.length - 1)} data-testid={`${testid}-add-line`}>
            <Plus className="h-3.5 w-3.5 mr-1" />{fl("tlAddRow", lang)}
          </Button>
          {footer?.(td, tc)}
        </div>
        <span className="col-span-6 md:col-span-3 num text-right" data-testid={`${testid}-total-dr`}>{fl("tlTotalDr", lang)}: {money(td, cur, lang)}</span>
        <span className="col-span-6 md:col-span-3 num text-right" data-testid={`${testid}-total-cr`}>{fl("tlTotalCr", lang)}: {money(tc, cur, lang)}</span>
      </div>
      <p className="px-3 py-1.5 text-[10px] text-muted-foreground" data-testid={`${testid}-kbd-hint`}>{fl("tlHint", lang)}</p>
    </div>
  );
}
